import { SeasonConfig, SeasonalSemanticColors } from '../types';
import { BackgroundTint } from './background-tint';
import { FallingLeaves } from './falling-leaves';
import { FlyingGhosts } from './flying-ghosts';
import SpookyLogoDark from './images/logo-dark.png';
import SpookyLogoWhite from './images/logo-white.png';
import HauntedHouseSound from './sounds/HauntedHouse.mp3';

/**
 * Fall/Halloween Theme - Semantic Color Palette
 *
 * Design intent:
 * - Dark, spooky backgrounds with warm undertones
 * - Orange as the primary accent (pumpkins, autumn leaves)
 * - Warm gray text colors for comfortable dark mode reading
 */
const fallSemanticColors: SeasonalSemanticColors = {
	// Primary accent - Halloween orange
	primary: {
		fg: '#ea580c', // orange-600
		fgStrong: '#c2410c', // orange-700
		fgStronger: '#9a3412', // orange-800
		fgStrongest: '#7c2d12', // orange-900
		bg: '#ea580c',
		bgHover: '#c2410c',
		bgActive: '#9a3412',
		bgAlpha: 'rgba(234, 88, 12, 0.12)',
		bgAlphaHover: 'rgba(234, 88, 12, 0.20)',
		bgAlphaActive: 'rgba(234, 88, 12, 0.28)',
	},

	// Backgrounds - Deep dark with warm undertones
	bg: {
		canvas: '#0a0a0a',
		surface: '#1a1a1a',
		soft: '#2a2a2a',
		subtle: '#1f1f1f',
		topNav: '#0f0f0f',
		inset: '#0d0d0d',
	},

	// Text - Warm grays for dark mode
	text: {
		default: '#ffffff',
		soft: '#d1d5db',
		softer: '#9ca3af',
		softest: '#6b7280',
		onColor: '#ffffff',
		interactive: '#ea580c',
	},

	// Borders
	border: {
		default: '#3a3a3a',
		soft: '#333333',
	},

	// Inverted gray scale for dark theme
	gray: {
		25: '#2a2a2a',
		50: '#2d3039',
		100: '#3c3f49',
		200: '#515661',
		300: '#656c7b',
		400: '#898f9a',
		500: '#a6acb5',
		600: '#c6cbd2',
		700: '#dbdfe6',
		800: '#edeef3',
		900: '#f6f6f9',
	},

	// Base color swaps
	white: '#242424',
	black: '#ffffff',
};

/**
 * Fall/Halloween Theme Configuration
 * Defines all assets, text, colors, and components for the fall seasonal theme
 */
export const fallConfig: SeasonConfig = {
	// Theme identification
	themeClassName: 'halloween-theme',
	toggleEmoji: '🎃',

	// Logo assets
	logoLight: SpookyLogoWhite, // Used in navbar (light background)
	logoDark: SpookyLogoDark, // Used in modal (dark background)

	// Modal assets
	modalSound: HauntedHouseSound,
	modalSoundVolume: 0.3,

	// Complete semantic color overrides
	semanticColors: fallSemanticColors,

	// Button text replacements
	buttonTexts: [
		'🎃 Give re-boo-gnition',
		'🕯️ Summon appreciation',
		'🧙 Cast recognition',
		'🍬 Treat someone',
		'🦇 Send a Boo-nus',
		'🔮 Conjure recognition',
		'💀 Give re-cognition',
		'Conjure some kudos 🧙',
		'Spread spooky vibes 🦇',
		'Summon some praise 🌙',
	],

	// Modal decorative emojis
	modalDecorations: [
		{ emoji: '🎃', top: '10px', left: '20px', fontSize: '40px', opacity: 0.2, rotation: -15 },
		{ emoji: '🦇', top: '30px', right: '30px', fontSize: '30px', opacity: 0.15, rotation: 15 },
		{ emoji: '👻', bottom: '40px', left: '30px', fontSize: '25px', opacity: 0.15, rotation: -10 },
		{ emoji: '🍂', top: '60px', left: '60%', fontSize: '20px', opacity: 0.1, rotation: 25 },
	],

	// Modal colors (for modal overlay, content, buttons)
	modalColors: {
		overlayBg: 'blackAlpha.800',
		contentBg: 'black',
		borderColor: 'orange.500',
		boxShadow: '0 20px 60px rgba(255, 107, 53, 0.5)',
		logoDropShadowColor: 'rgba(255, 107, 53, 0.6)', // Orange glow for Halloween
		subtitleColor: 'orange.200',
		enableButtonBg: 'orange.600',
		enableButtonHoverBg: 'orange.500',
		enableButtonActiveBg: 'orange.700',
		enableButtonBoxShadow: '0 4px 15px rgba(255, 107, 53, 0.4)',
		enableButtonHoverBoxShadow: '0 8px 25px rgba(255, 107, 53, 0.6)',
		cancelButtonColor: 'orange.300',
		cancelButtonHoverColor: 'orange.200',
		noticeBg: 'orange.900',
		noticeBorderColor: 'orange.600',
		noticeTextColor: 'orange.200',
	},

	// Modal text content
	modalText: {
		subtitle: 'Transform your Bonusly experience with our spooky Halloween theme! 🎃',
		features: [
			{
				emoji: '🍂',
				text: 'Animated falling leaves, pumpkins, and friendly ghosts that dance across your screen',
			},
			{
				emoji: '🎨',
				text: 'Dark mode with orange accents and spooky vibes throughout the interface',
			},
			{
				emoji: '✨',
				text: 'Fun Halloween-themed recognition messages and seasonal surprises',
			},
		],
		endDateNotice: 'This spooky theme will be available through the end of October. Toggle it on/off anytime!',
		enableButtonText: 'Enable Halloween Theme',
		cancelButtonText: 'Maybe Later',
	},

	// Visual effects components
	EffectsComponent: () => (
		<>
			<BackgroundTint />
			<FallingLeaves />
			<FlyingGhosts />
		</>
	),
};
